
class PDPSetItem extends HTMLElement {
  constructor() {
    super();
    this._delegateEvents();
  }

  get addButton() { return this.querySelector('[data-add-all-to-bag]'); }
  get errorText() { return this.querySelector('[data-error-text]'); }
  get items() { return this.querySelectorAll('.set-recommendation-item-container'); }

  connectedCallback() {
    this._updateActionButtonState();
  }

  _delegateEvents() {
    // 1. Size changes (delegated)
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"][name*="size"]')) {
        this._onSizeChange(event);
      }
    });

    // 2. Click events (delegated)
    this.addEventListener('click', (event) => {
      // Swatch clicks
      const swatchBtn = event.target.closest('.set-recommendation-swatch-btn');
      if (swatchBtn) {
        this._handleSwatchClick(swatchBtn);
        return;
      }

      // Add to bag button
      if (event.target.closest('[data-add-all-to-bag]')) {
        this._addToBag();
      }
    });
  }

  _handleSwatchClick(swatchBtn) {
    if (swatchBtn.classList.contains('active')) return;

    const itemElement = swatchBtn.closest('.set-recommendation-item-container');
    const productJson = swatchBtn.getAttribute('data-product-json');

    if (!itemElement || !productJson) return;

    try {
      const productData = JSON.parse(productJson);
      this._updateItemProduct(itemElement, productData);

      // Update active state in current list
      const list = swatchBtn.closest('[data-swatches-list]');
      list?.querySelectorAll('.set-recommendation-swatch-btn').forEach(btn => btn.classList.remove('active'));
      swatchBtn.classList.add('active');
    } catch (e) {
      console.error("Error parsing product JSON:", e);
    }
  }

  _updateActionButtonState() {
    const addButton = this.addButton;
    const addText = addButton?.querySelector('.set-recommendation-add-text');
    const items = this.items;

    if (!addText || !items.length) return;

    const topItem = items[0];
    const bottomItem = items[1];

    const topSelected = topItem?.querySelector('input[type="radio"]:checked');
    const bottomSelected = bottomItem?.querySelector('input[type="radio"]:checked');

    if (topSelected && bottomSelected) {
      addButton?.classList.remove('disabled');
      addText.textContent = 'ADD BUNDLE TO CART';
      this.errorText?.classList.add('d-none');
    } else {
      addButton?.classList.add('disabled');

      if (!topSelected && !bottomSelected) {
        addText.textContent = 'Select size';
      } else if (topSelected && !bottomSelected) {
        addText.textContent = 'Select bottom size';
      } else if (!topSelected && bottomSelected) {
        addText.textContent = 'Select top size';
      }
    }
  }

  _updateItemProduct(itemElement, product) {
    const productId = product.id;
    const productHandle = product.handle;

    // 1. Update Image
    const img = itemElement.querySelector('.set-recommendation-item-image img');
    if (img && product.featured_image) {
      img.src = product.featured_image;
      img.alt = product.title;
    }

    // 2. Update Title
    const title = itemElement.querySelector('.set-recommendation-product-title');
    if (title) {
      title.textContent = product.title;
    }

    // 3. Update Size Swatches
    const sizeWrapper = itemElement.querySelector('[data-size-wrapper]');
    const optIndex = product.options.findIndex(o => {
      const name = typeof o === 'string' ? o : o.name;
      return name && name.toLowerCase() === 'size';
    });

    if (sizeWrapper && optIndex !== -1) {
      const optionKey = `option${optIndex + 1}`;
      const sizes = [...new Set(product.variants.map(v => v[optionKey]))];

      const sizeItemsHtml = sizes.map((val) => {
        const variant = product.variants.find(v => v[optionKey] === val && v.available);
        const displayVariant = product.variants.find(v => v[optionKey] === val) || variant;

        if (!displayVariant) return '';

        const isDisabled = !variant;
        const swatchID = `swatch-${productId}-setrec-${val.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

        return `
            <li class="set-recommendation-variant-size variant-size ${isDisabled ? 'unavailable' : ''}">
                <div class="set-recommendation-swatch">
                    <input type="radio" 
                           class="form-control d-none" 
                           id="${swatchID}" 
                           name="size-${productId}" 
                           value="${val}" 
                           data-upsell-id="${displayVariant.id}" 
                           data-upsell-product-id="${productId}" 
                           ${isDisabled ? 'disabled' : ''}>
                    <label class="set-recommendation-size-swatch-label option_txt text-uppercase fw-bold d-flex align-items-center justify-content-center" 
                           for="${swatchID}" 
                           title="${val}">
                        ${val}
                    </label>
                </div>
            </li>
        `;
      }).join('');

      sizeWrapper.innerHTML = `
                <fieldset class="js product-form__input p-0 border-0">
                    <ul class="list-unstyled mb-0 set-recommendation-size-list product-size-list d-flex flex-wrap gap-2">
                        ${sizeItemsHtml}
                    </ul>
                </fieldset>
            `;
    }

    // 4. Update data attributes
    itemElement.setAttribute('data-product-id', productId);
    itemElement.setAttribute('data-product-handle', productHandle);

    // 5. Reset button state
    this._updateActionButtonState();
  }

  _onSizeChange() {
    this._updateActionButtonState();
  }

  async _addToBag() {
    const selectedSizeInputs = this.querySelectorAll('input[type="radio"]:checked');
    const items = this.items;
    const addButton = this.addButton;

    if (selectedSizeInputs.length < items.length) {
      this.errorText?.classList.remove('d-none');
      return;
    }

    if (addButton?.classList.contains('loading')) return;
    addButton?.classList.add('loading');

    let formData = {
      'items': [],
      sections: `${((window.globalVariables?.template === 'cart') ? 'template-cart' : 'component-cart-drawer')},header`
    };

    selectedSizeInputs.forEach(input => {
      const variantId = input.getAttribute('data-upsell-id');
      const itemContainer = input.closest('.set-recommendation-item-container');

      let properties = {
        "_upsell": true,
        "_source": "bundle_set_save"
      };

      formData.items.push({
        id: variantId,
        quantity: 1,
        properties: properties
      });
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.status === 'error') throw new Error(data.description);

      const cartFormEle = document.querySelector('cart-form');
      if (cartFormEle && data.sections) {
        cartFormEle._updateCart(data.sections, 'open_drawer');
      }
    } catch (error) {
      console.error("Add to bag error:", error);
      if (this.errorText) {
        this.errorText.textContent = error.message;
        this.errorText.classList.remove('d-none');
      }
    } finally {
      addButton?.classList.remove('loading');
    }
  }
}

if (!customElements.get('pdp-set-item')) {
  customElements.define('pdp-set-item', PDPSetItem);
}
